import React, { useState, useRef, useEffect } from 'react';
import { 
  Type, 
  Volume2, 
  VolumeX, 
  Search,
  Compass,
  Home,
  Users,
  Feather,
  Heart,
  Book,
  ChevronDown,
  ChevronRight,
  X,
  Ship,
  Package,
  Globe,
  Hammer,
  Menu
} from 'lucide-react';
import { AccessibilitySettings, ArchiveTab, TAB_TO_PATH } from '../types';
import { useVoiceReader } from '../context/VoiceReaderContext';

interface NavbarProps {
  activeTab: ArchiveTab;
  setActiveTab: (tab: ArchiveTab) => void;
  accessibility: AccessibilitySettings;
  setAccessibility: React.Dispatch<React.SetStateAction<AccessibilitySettings>>;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
}

export interface NavItemConfig {
  id: ArchiveTab;
  label: string;
  icon: React.ReactNode;
  isParent?: boolean;
  subItems?: {
    id: ArchiveTab;
    label: string;
    description: string;
    icon: React.ReactNode;
  }[];
}

export const NAV_ITEMS: NavItemConfig[] = [
  { 
    id: 'homepage', 
    label: 'Homepage', 
    icon: <Feather className="w-4 h-4" /> 
  },
  { 
    id: 'journey', 
    label: 'The Journey', 
    icon: <Compass className="w-4 h-4" />,
    isParent: true,
    subItems: [
      {
        id: 'journey-suffolk',
        label: 'From Suffolk to the Southern Seas',
        description: 'The 1850 voyage and Suffolk roots',
        icon: <Ship className="w-4 h-4 text-[#7a5b3a]" />
      },
      {
        id: 'journey-essentials',
        label: 'Essentials for a new land',
        description: '1850 outfitting, wardrobe and provisions',
        icon: <Package className="w-4 h-4 text-[#7a5b3a]" />
      },
      {
        id: 'journey-why-they-came',
        label: 'Why they came around the curve of the Earth',
        description: 'Motivations, Wakefield scheme and early settlement',
        icon: <Globe className="w-4 h-4 text-[#7a5b3a]" />
      }
    ]
  },
  { 
    id: 'dentons', 
    label: 'The Dentons', 
    icon: <Home className="w-4 h-4" /> 
  },
  { 
    id: 'gibbs', 
    label: 'The Gibb Family', 
    icon: <Users className="w-4 h-4" /> 
  },
  { 
    id: 'giles', 
    label: 'The Giles Family', 
    icon: <Hammer className="w-4 h-4" /> 
  },
  { 
    id: 'roberts', 
    label: 'The Roberts Family', 
    icon: <Book className="w-4 h-4" /> 
  },
  { 
    id: 'acknowledgement', 
    label: 'Acknowledgement and Thanks', 
    icon: <Heart className="w-4 h-4" /> 
  },
];

export const Navbar: React.FC<NavbarProps> = ({
  activeTab,
  setActiveTab,
  accessibility,
  setAccessibility,
  searchQuery,
  setSearchQuery
}) => {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState<boolean>(false);
  const [mobileJourneyAccordion, setMobileJourneyAccordion] = useState<boolean>(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  // Prevent background scrolling when mobile drawer is open
  useEffect(() => {
    if (mobileDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileDrawerOpen]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  const {
    isActive,
    toggleActive,
    isPlaying,
    isPaused,
    speakCurrentPage,
    pause,
    resume
  } = useVoiceReader();

  const toggleSpeech = () => {
    if (!isActive) {
      toggleActive();
      setAccessibility(prev => ({ ...prev, textToSpeechEnabled: true }));
      speakCurrentPage();
    } else if (isPlaying) {
      pause();
    } else if (isPaused) {
      resume();
    } else {
      speakCurrentPage();
    }
  };

  const isJourneyActive = activeTab === 'journey' || 
    activeTab === 'journey-suffolk' || 
    activeTab === 'journey-essentials' || 
    activeTab === 'journey-why-they-came';

  const handleNavigate = (tab: ArchiveTab) => {
    setActiveTab(tab);
    if (searchQuery) setSearchQuery('');
    setDropdownOpen(false);
    setMobileDrawerOpen(false);
  };

  return (
    <header className="no-print bg-[#fbf9f4] border-b-2 border-[#d8ceb9] sticky top-0 z-40 shadow-xs">
      
      {/* Top Archival Bar */}
      <div className="bg-[#2c221e] text-[#fbf9f4] px-3 sm:px-4 py-2 text-xs md:text-sm border-b border-[#4a3525]">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 font-serif min-w-0">
            <span className="bg-[#7a5b3a] text-amber-100 px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs font-bold uppercase tracking-wider shrink-0">
              Archival Compilation
            </span>
            <span className="hidden md:inline text-amber-200/80 text-xs truncate">
              Christine Negus • Stories from the First Family Members in New Zealand
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Voice Narrator Button (min 44px touch target on mobile via padding & container) */}
            <button
              onClick={toggleSpeech}
              title={
                isPlaying
                  ? 'Voice Reader Speaking (Click to Pause)'
                  : isPaused
                  ? 'Voice Reader Paused (Click to Resume)'
                  : isActive
                  ? 'Voice Reader Active (Click to Start Narration)'
                  : 'Enable Voice Reader (Narrate Page)'
              }
              className={`min-h-[38px] sm:min-h-[40px] flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition shadow-xs cursor-pointer ${
                isPlaying
                  ? 'bg-emerald-700 text-white ring-2 ring-emerald-400/50'
                  : isPaused
                  ? 'bg-amber-700 text-white'
                  : isActive
                  ? 'bg-emerald-800 text-emerald-100'
                  : 'bg-[#4a3525] text-amber-200/80 hover:text-white border border-amber-200/20'
              }`}
            >
              {isPlaying ? (
                <>
                  <Volume2 className="w-4 h-4 text-emerald-200 animate-pulse shrink-0" />
                  <span className="hidden xs:inline">Narrating...</span>
                  <span className="xs:hidden">Playing</span>
                </>
              ) : isPaused ? (
                <>
                  <Volume2 className="w-4 h-4 text-amber-300 shrink-0" />
                  <span className="hidden xs:inline">Paused</span>
                  <span className="xs:hidden">Resume</span>
                </>
              ) : isActive ? (
                <>
                  <Volume2 className="w-4 h-4 text-emerald-300 shrink-0" />
                  <span className="hidden xs:inline">Voice ON</span>
                  <span className="xs:hidden">ON</span>
                </>
              ) : (
                <>
                  <VolumeX className="w-4 h-4 shrink-0" />
                  <span className="hidden xs:inline">Voice Reader</span>
                  <span className="xs:hidden">Voice</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Branding & Search Header */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-3 sm:pt-4 pb-3 space-y-3 sm:space-y-4">
        <div className="flex items-center justify-between gap-3">
          
          {/* Logo & Archive Name */}
          <div 
            onClick={() => handleNavigate('homepage')}
            className="flex items-center gap-2.5 sm:gap-3.5 cursor-pointer group min-w-0 flex-1"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-[#4a3525] text-amber-100 flex items-center justify-center shadow-md shrink-0 border border-amber-200/30 group-hover:bg-[#634833] transition">
              <span className="font-serif text-xl sm:text-2xl font-bold">CN</span>
            </div>
            <div className="min-w-0">
              <h1 className="font-serif text-lg sm:text-2xl md:text-3xl font-bold text-[#2c221e] tracking-tight group-hover:text-[#7a5b3a] transition truncate">
                Stories from the First Family Members in New Zealand
              </h1>
              <p className="text-[11px] sm:text-xs md:text-sm text-[#7a5b3a] font-serif font-medium truncate">
                Digital Family History Compilation • Christine Negus
              </p>
            </div>
          </div>

          {/* Desktop Search Input (hidden on mobile, visible on md+) */}
          <div className="relative hidden md:block w-72 lg:w-84 shrink-0">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7a5b3a] pointer-events-none transition-colors" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search compilation text, names, dates..."
              aria-label="Search compilation text, names, and dates"
              className="w-full pl-10 pr-10 py-2.5 text-sm bg-[#f3ede0] hover:bg-[#ede4d2] focus:bg-white border-2 border-[#d8ceb9] focus:border-[#7a5b3a] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7a5b3a]/30 text-[#2c221e] placeholder-[#7a5b3a]/70 font-medium transition-all shadow-inner"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-lg text-[#7a5b3a] hover:text-[#2c221e] hover:bg-[#d8ceb9]/50 transition cursor-pointer flex items-center justify-center"
                title="Clear search query"
                aria-label="Clear search query"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Mobile Hamburger Toggle Button (min 44x44px target area) */}
          <div className="flex items-center gap-1.5 md:hidden">
            <button
              onClick={() => setMobileDrawerOpen(true)}
              aria-label="Open Navigation Menu"
              className="min-h-[44px] min-w-[44px] p-2.5 rounded-xl bg-[#f3ede0] border border-[#d8ceb9] text-[#2c221e] hover:bg-[#e8dec9] active:bg-[#d8ceb9] transition flex items-center justify-center cursor-pointer shadow-xs"
            >
              <Menu className="w-6 h-6 text-[#4a3525]" />
            </button>
          </div>
        </div>

        {/* Mobile Search Bar (under logo on small screens) */}
        <div className="relative md:hidden w-full">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7a5b3a] pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search compilation text, names, dates..."
            aria-label="Search compilation text, names, and dates"
            className="w-full pl-10 pr-10 py-2.5 text-sm bg-[#f3ede0] hover:bg-[#ede4d2] focus:bg-white border-2 border-[#d8ceb9] focus:border-[#7a5b3a] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7a5b3a]/30 text-[#2c221e] placeholder-[#7a5b3a]/70 font-medium transition-all shadow-inner min-h-[44px]"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#7a5b3a] hover:text-[#2c221e] hover:bg-[#d8ceb9]/50 rounded-lg cursor-pointer min-h-[36px] min-w-[36px] flex items-center justify-center transition"
              title="Clear search query"
              aria-label="Clear search query"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Desktop Hierarchical Navigation Bar (Horizontal on md+ screens) */}
        <nav aria-label="Main Navigation" className="hidden md:flex items-center gap-1.5 overflow-x-auto pb-1 border-t border-[#d8ceb9] pt-3 relative scrollbar-none">
          
          {NAV_ITEMS.map((item) => {
            if (item.isParent && item.subItems) {
              return (
                <div
                  key={item.id}
                  ref={dropdownRef}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  className="relative shrink-0"
                >
                  <div className="flex items-center">
                    {/* Main Parent Button */}
                    <button
                      onClick={() => handleNavigate(item.id)}
                      className={`min-h-[44px] pl-3.5 pr-2 py-2 rounded-l-xl text-xs lg:text-sm font-serif font-bold flex items-center gap-1.5 whitespace-nowrap transition cursor-pointer ${
                        isJourneyActive && !searchQuery
                          ? 'bg-[#4a3525] text-amber-100 shadow-sm border-t border-b border-l border-amber-200/30 ring-2 ring-[#7a5b3a]/30 z-10'
                          : 'bg-[#f3ede0] text-[#2c221e] border-t border-b border-l border-[#d8ceb9] hover:bg-[#e8dec9]'
                      }`}
                    >
                      <span className={isJourneyActive ? 'text-amber-300' : 'text-[#7a5b3a]'}>
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                    </button>

                    {/* Dropdown Toggle Trigger Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(prev => !prev);
                      }}
                      aria-expanded={dropdownOpen}
                      aria-haspopup="true"
                      title="Explore The Journey Sub-chapters"
                      className={`min-h-[44px] px-2.5 py-2 rounded-r-xl text-xs lg:text-sm font-serif font-bold flex items-center transition cursor-pointer ${
                        isJourneyActive && !searchQuery
                          ? 'bg-[#4a3525] text-amber-100 shadow-sm border-t border-b border-r border-amber-200/30 ring-2 ring-[#7a5b3a]/30'
                          : 'bg-[#f3ede0] text-[#7a5b3a] border-t border-b border-r border-[#d8ceb9] hover:bg-[#e8dec9]'
                      }`}
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180 text-amber-300' : ''}`} />
                    </button>
                  </div>

                  {/* Dropdown Submenu Menu */}
                  {dropdownOpen && (
                    <div className="absolute top-full left-0 mt-1.5 w-80 lg:w-96 bg-[#fbf9f4] border-2 border-[#d8ceb9] rounded-2xl shadow-xl p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                      
                      {/* Hub Page Quick link */}
                      <button
                        onClick={() => handleNavigate('journey')}
                        className={`w-full text-left p-3 rounded-xl transition flex items-start gap-3 border cursor-pointer ${
                          activeTab === 'journey' && !searchQuery
                            ? 'bg-[#f3ede0] border-[#7a5b3a]'
                            : 'hover:bg-[#f3ede0] border-transparent'
                        }`}
                      >
                        <div className="p-2 bg-[#e8dec9] rounded-lg text-[#7a5b3a] shrink-0 mt-0.5">
                          <Compass className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-serif font-bold text-sm text-[#2c221e]">
                            The Journey — Overview Hub
                          </p>
                          <p className="text-xs text-[#7a5b3a] leading-tight mt-0.5">
                            Table of contents & summary of the 1850 Canterbury migration
                          </p>
                        </div>
                      </button>

                      <div className="my-1.5 border-t border-[#d8ceb9]" />

                      {/* 3 Sub-pages */}
                      <div className="space-y-1">
                        {item.subItems.map((sub) => {
                          const isSubActive = activeTab === sub.id && !searchQuery;
                          return (
                            <button
                              key={sub.id}
                              onClick={() => handleNavigate(sub.id)}
                              className={`w-full text-left p-3 rounded-xl transition flex items-start gap-3 border cursor-pointer ${
                                isSubActive
                                  ? 'bg-[#4a3525] text-amber-100 border-amber-200/30'
                                  : 'hover:bg-[#f3ede0] text-[#2c221e] border-transparent'
                              }`}
                            >
                              <div className={`p-2 rounded-lg shrink-0 mt-0.5 ${
                                isSubActive ? 'bg-[#634833] text-amber-300' : 'bg-[#e8dec9] text-[#7a5b3a]'
                              }`}>
                                {sub.icon}
                              </div>
                              <div>
                                <p className={`font-serif font-bold text-sm ${isSubActive ? 'text-amber-100' : 'text-[#2c221e]'}`}>
                                  {sub.label}
                                </p>
                                <p className={`text-xs leading-tight mt-0.5 ${isSubActive ? 'text-amber-200/80' : 'text-[#7a5b3a]'}`}>
                                  {sub.description}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>

                    </div>
                  )}
                </div>
              );
            }

            // Standard Single Tab
            const isActive = activeTab === item.id && !searchQuery;
            return (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                className={`min-h-[44px] px-3.5 py-2 rounded-xl text-xs lg:text-sm font-serif font-bold flex items-center gap-2 whitespace-nowrap transition shrink-0 cursor-pointer ${
                  isActive
                    ? 'bg-[#4a3525] text-amber-100 shadow-sm border border-amber-200/30 ring-2 ring-[#7a5b3a]/30'
                    : 'bg-[#f3ede0] text-[#2c221e] border border-[#d8ceb9] hover:bg-[#e8dec9]'
                }`}
              >
                <span className={isActive ? 'text-amber-300' : 'text-[#7a5b3a]'}>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </button>
            );
          })}

        </nav>
      </div>

      {/* Mobile Drawer / Off-canvas Menu Overlay */}
      {mobileDrawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          {/* Backdrop Blur */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity duration-300"
            onClick={() => setMobileDrawerOpen(false)}
          />

          {/* Drawer Panel */}
          <div className="relative ml-auto w-[85%] max-w-sm bg-[#fbf9f4] h-full shadow-2xl flex flex-col z-10 border-l-2 border-[#d8ceb9] overflow-y-auto animate-in slide-in-from-right duration-200">
            
            {/* Drawer Header */}
            <div className="bg-[#2c221e] text-white p-4 flex items-center justify-between border-b border-[#4a3525] sticky top-0 z-20">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-[#4a3525] text-amber-100 flex items-center justify-center font-serif font-bold text-sm border border-amber-200/30">
                  CN
                </div>
                <div>
                  <h2 className="font-serif font-bold text-sm text-amber-100">
                    Family Archives
                  </h2>
                  <p className="text-[10px] text-amber-200/70">
                    Christine Negus Compilation
                  </p>
                </div>
              </div>

              <button
                onClick={() => setMobileDrawerOpen(false)}
                aria-label="Close menu"
                className="min-h-[44px] min-w-[44px] p-2 rounded-xl bg-white/10 hover:bg-red-600 text-white transition flex items-center justify-center cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Navigation Links */}
            <div className="p-4 space-y-3 flex-1">
              <div className="text-[11px] font-serif uppercase tracking-wider text-[#7a5b3a] font-bold px-1">
                Chapters &amp; Sections
              </div>

              <div className="space-y-2">
                {NAV_ITEMS.map((item) => {
                  if (item.isParent && item.subItems) {
                    return (
                      <div key={item.id} className="rounded-2xl border border-[#d8ceb9] overflow-hidden bg-[#f3ede0]">
                        {/* Parent Accordion Header */}
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => handleNavigate('journey')}
                            className={`min-h-[48px] flex-1 text-left px-4 py-3 flex items-center gap-2.5 font-serif font-bold text-sm transition cursor-pointer ${
                              isJourneyActive && !searchQuery
                                ? 'text-[#7a5b3a]'
                                : 'text-[#2c221e]'
                            }`}
                          >
                            <span className="text-[#7a5b3a]">{item.icon}</span>
                            <span>{item.label}</span>
                          </button>
                          
                          <button
                            onClick={() => setMobileJourneyAccordion(prev => !prev)}
                            aria-label="Toggle Journey sub-chapters"
                            className="min-h-[48px] min-w-[48px] px-3 flex items-center justify-center text-[#7a5b3a] hover:bg-[#e8dec9] cursor-pointer"
                          >
                            <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${mobileJourneyAccordion ? 'rotate-180' : ''}`} />
                          </button>
                        </div>

                        {/* Accordion Content */}
                        {mobileJourneyAccordion && (
                          <div className="bg-[#fbf9f4] border-t border-[#d8ceb9] p-2 space-y-1.5">
                            <button
                              onClick={() => handleNavigate('journey')}
                              className={`min-h-[44px] w-full text-left px-3 py-2.5 rounded-xl text-xs font-serif font-bold flex items-center gap-2 transition cursor-pointer ${
                                activeTab === 'journey' && !searchQuery
                                  ? 'bg-[#4a3525] text-amber-100 shadow-sm'
                                  : 'text-[#2c221e] hover:bg-[#e8dec9]'
                              }`}
                            >
                              <Compass className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                              <span>Overview Hub</span>
                            </button>

                            {item.subItems.map((sub) => {
                              const isSubActive = activeTab === sub.id && !searchQuery;
                              return (
                                <button
                                  key={sub.id}
                                  onClick={() => handleNavigate(sub.id)}
                                  className={`min-h-[44px] w-full text-left px-3 py-2.5 rounded-xl text-xs font-serif font-bold flex items-center gap-2 transition cursor-pointer ${
                                    isSubActive
                                      ? 'bg-[#4a3525] text-amber-100 shadow-sm'
                                      : 'text-[#2c221e] hover:bg-[#e8dec9]'
                                  }`}
                                >
                                  <span className="shrink-0">{sub.icon}</span>
                                  <span className="truncate">{sub.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  const isActive = activeTab === item.id && !searchQuery;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavigate(item.id)}
                      className={`min-h-[48px] w-full text-left px-4 py-3 rounded-2xl font-serif font-bold text-sm flex items-center justify-between gap-2.5 transition cursor-pointer border ${
                        isActive
                          ? 'bg-[#4a3525] text-amber-100 border-[#4a3525] shadow-sm'
                          : 'bg-[#f3ede0] text-[#2c221e] border-[#d8ceb9] hover:bg-[#e8dec9]'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className={isActive ? 'text-amber-300' : 'text-[#7a5b3a]'}>
                          {item.icon}
                        </span>
                        <span>{item.label}</span>
                      </div>
                      <ChevronRight className={`w-4 h-4 ${isActive ? 'text-amber-300' : 'text-[#7a5b3a]'}`} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Drawer Footer Actions */}
            <div className="p-4 bg-[#f3ede0] border-t border-[#d8ceb9] space-y-2">
              <button
                onClick={() => {
                  toggleSpeech();
                  setMobileDrawerOpen(false);
                }}
                className={`min-h-[44px] w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition shadow-xs cursor-pointer ${
                  isPlaying
                    ? 'bg-emerald-700 text-white'
                    : 'bg-[#4a3525] text-amber-100 hover:bg-[#634833]'
                }`}
              >
                <Volume2 className="w-4 h-4 text-amber-300" />
                <span>{isPlaying ? 'Pause Voice Reader' : 'Start Voice Narration'}</span>
              </button>
            </div>

          </div>
        </div>
      )}

    </header>
  );
};

